import { Link } from "react-router-dom";
import { ArrowUpRight, Puzzle } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ROUTE_DEMO } from "@/lib/brand";
import {
  CUSTOM_DELIVERABLES,
  CUSTOM_DELIVERABLES_TITLE,
  LEVELS,
  MEASURE_CLAIM,
} from "@/lib/measurement";

// Fotos von Unsplash, lokal ausgeliefert (keine Verbindung zu Dritten)
const IMG_STADIUM = `${process.env.PUBLIC_URL}/assets/photos/stadion-abend.jpg`;
const IMG_WEIGHTS = `${process.env.PUBLIC_URL}/assets/photos/training.jpg`;

export const Measurability = () => {
  return (
    <section
      id="messbarkeit"
      data-testid="measurability-section"
      className="relative py-24 lg:py-32 border-y border-white/[0.06]"
    >
      <div className="px-6 md:px-10 lg:px-16">
        <Reveal className="max-w-2xl mb-14">
          <p className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-brand-orange mb-5">
            Der Kern
          </p>
          <h2 className="font-display font-bold tracking-tight text-4xl md:text-5xl lg:text-6xl leading-[0.98]">
            Sponsoring war Vertrauenssache.{" "}
            <span className="gradient-text">Jetzt ist es belegbar</span>.
          </h2>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-white/55">
            Alles andere auf dieser Plattform dient diesem einen Punkt: dass am
            Ende einer Saison beide Seiten wissen, was aus der Partnerschaft
            geworden ist.
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-6">
          <Reveal className="lg:col-span-7">
            <div className="group relative rounded-3xl overflow-hidden border border-white/10 h-full min-h-[460px]">
              <img
                src={IMG_STADIUM}
                alt="Stadion bei Nacht"
                className="absolute inset-0 w-full h-full object-cover opacity-45 group-hover:opacity-60 group-hover:scale-105 transition-all duration-[900ms]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/75 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-8 md:p-12">
                <span className="text-xs uppercase tracking-[0.2em] font-bold text-white/50 mb-5">
                  Drei Ebenen
                </span>
                <p className="font-display font-bold text-3xl md:text-4xl leading-tight text-white max-w-xl">
                  {MEASURE_CLAIM}
                </p>
                <ul className="mt-8 grid gap-3 sm:grid-cols-3">
                  {LEVELS.map(({ icon: Icon, title, no, short }) => (
                    <li
                      key={title}
                      className="rounded-2xl border border-white/15 bg-navy-950/50 px-4 py-3.5"
                    >
                      <div className="flex items-center gap-3">
                        <span className="flex items-center justify-center w-8 h-8 shrink-0 rounded-lg bg-white/[0.06] border border-white/10">
                          <Icon className="w-4 h-4 text-brand-orange" />
                        </span>
                        <span>
                          <span className="block text-[10px] uppercase tracking-[0.18em] font-bold text-white/40">
                            Ebene {no}
                          </span>
                          <span className="block text-sm font-semibold text-white">
                            {title}
                          </span>
                        </span>
                      </div>
                      <p className="mt-2.5 text-xs leading-relaxed text-white/60 hyphens-auto break-words">
                        {short}
                      </p>
                    </li>
                  ))}
                </ul>
                <Link
                  to={ROUTE_DEMO}
                  className="group/link mt-8 inline-flex items-center gap-2 self-start text-sm font-bold text-white"
                >
                  In der Demo ausprobieren
                  <ArrowUpRight className="w-4 h-4 group-hover/link:rotate-45 transition-transform duration-300" />
                </Link>
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-5 flex flex-col gap-6">
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-white/10 backdrop-blur-xl p-8">
                <span className="block text-[11px] uppercase tracking-[0.2em] font-bold text-white/35 mb-4">
                  Für das Unternehmen
                </span>
                <p className="font-display font-bold text-xl md:text-2xl text-white mb-3">
                  Ein Bericht statt eines Erinnerungsfotos.
                </p>
                <p className="text-base leading-relaxed text-white/60">
                  Was beim Abschluss vereinbart wurde, läuft über die gesamte
                  Saison mit: gemessen, wo es geht, eingetragen und belegt, wo es
                  anders nicht geht. Was am Ende bleibt, lässt sich intern
                  vorlegen.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="relative rounded-3xl overflow-hidden border border-white/10 flex-1 min-h-[220px] group">
                <img
                  src={IMG_WEIGHTS}
                  alt="Athlet im Training"
                  className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950 to-navy-950/30" />
                <div className="relative h-full flex flex-col justify-end p-8">
                  <span className="block text-[11px] uppercase tracking-[0.2em] font-bold text-white/45 mb-3">
                    Für den Verein
                  </span>
                  <p className="font-display font-bold text-xl md:text-2xl text-white mb-2">
                    Endlich ein Argument für die Verlängerung.
                  </p>
                  <p className="text-sm leading-relaxed text-white/60">
                    Am Spieltag in Sekunden eingetragen, vieles läuft
                    automatisch. Wer belegen kann, was ein Engagement gebracht
                    hat, muss im nächsten Sommer nicht bei null anfangen.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal className="lg:col-span-12" delay={0.1}>
            <div className="flex flex-col md:flex-row md:items-center gap-6 rounded-3xl border border-white/10 backdrop-blur-xl p-8 md:p-10">
              <span className="flex items-center justify-center w-12 h-12 shrink-0 rounded-xl bg-white/[0.06] border border-white/10">
                <Puzzle className="w-5 h-5 text-brand-blue" />
              </span>
              <div>
                <p className="font-display font-bold text-xl md:text-2xl text-white mb-2">
                  {CUSTOM_DELIVERABLES_TITLE}
                </p>
                <p className="max-w-4xl text-base leading-relaxed text-white/60">
                  {CUSTOM_DELIVERABLES}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
